import { Box, Button, CircularProgress, Collapse, DialogContent, FormControl, IconButton, MenuItem, Select, TextField, Tooltip, Typography, useTheme } from '@mui/material'
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import React, { useState } from 'react'
import { toast } from 'react-toastify'
import { observer } from 'mobx-react'
import * as Yup from 'yup'
import dayjs from 'dayjs'

import { useQuizAndQuestionbankStore } from '../../../../store/quiz.store'
import { formatDate, getClassQuizTitle, Icons, latexDelimiters, QUIZ_DURATION_OPTIONS } from '../../../../utils/utilities'
import { useUserStore } from '../../../../store/user.store'
import CustomSelectField from '../../../common/CustomSelectField'
import QuestionsPreviewDialog from '../../QuestionsPreviewDialog'
import { validateQuestionsImportFormatForQuestionBank } from '../../../../utils/exam-utilities'
import api from '../../../../service/api'
import { convert } from 'html-to-text'
import Latex from 'react-latex-next'



const CreateQuickQuiz = observer( ( { fetchQuizzesInstances, setNewBankId, setQuestionBankMode, questionBankMode, loadingSubjects, room, setAddMode, addMode, subject, handleClose, classType } ) => {

    const [hasNewQuestionBankSaved, setHasNewQuestionBankSaved] = useState( true )
    const [newQuestionBankDetails, setNewQuestionBankDetails] = useState( null )
    const [questionsPreview, setQuestionsPreview] = useState( false )
    const [importedQuestions, setImportedQuestions] = useState( [] )
    const [isImporting, setIsImporting] = useState( false )
    const [adding, setAdding] = useState( false )


    const UserStore = useUserStore()
    const QuizAndQuestionBankStore = useQuizAndQuestionbankStore()

    const { palette, border, components } = useTheme()

    const roomSubject = UserStore.getUserSubjectsDetails?.currentSubjects?.find( s => room.subject_id === s.subject_id )

    const initialValues = {
        name: getClassQuizTitle( room ),
        subject: roomSubject,
        question_bank_name: `${roomSubject.subject_name}-${formatDate( new Date(), { format: "DD-MM-YYYY" } )}-${room.batch_short_name || room.section_short_name}`,
        type: 'scheduled_quiz',
        start_time: room.sTime,
        end_time: room.eTime,
        access_group: room.batch_short_name !== "NA" ?
            { course_name_short: room.course_name_short, current_sem: room.current_sem, current_year: room.current_year, id: room.batch_assignment_id, batch_short_name: room.batch_short_name, batch_assignment_id: room.batch_assignment_id, branch_name: room.branch_name, branch_id: room.branch_id } :
            { course_branch_short_name: room.course_branch_short_name, current_sem: room.current_sem, current_year: room.current_year, id: `${room.section_assignment_id}-${room.section_short_name}`, section_assignment_id: room.section_assignment_id, section_short_name: room.section_short_name, branch_name: room.branch_name, branch_id: room.branch_id },
        start_date: new Date( room.selected_date ),
        maximum_attempts: 1,
        room_id: room.time_table_id,
        use_selected_topic_question: false,
        quiz_questions: [],
        quiz_duration: '00:10',
        max_questions: 0
    }

    const validationSchema = Yup.object().shape( {
        subject: Yup.mixed().notOneOf( ["-"], "Specify the quiz subject" ).required( "Subject is required." ),
        access_group: Yup.mixed().test( "CHAK_FOR_ACCESS_GROUP", "Access groups are required.", ( val, { parent } ) => {
            if ( parent.type !== "scheduled-quiz" ) return true
            if ( !val || val === "-" ) return false
            return true
        } ),
        start_time: Yup.string().test( "TEST_FOR_START_TIME", "For a scheduled quiz a valid start time is required.", ( val, { parent } ) => {
            try {
                if ( parent.type !== "scheduled-quiz" ) return true
                if ( !val || val.length === 0 || !val.match( /^(\d\d:\d\d)$/ ) ) return false
                return true
            } catch ( err ) {
                return true
            }
        } ),
        end_time: Yup.string().test( "TEST_FOR_END_TIME", "For a scheduled quiz a valid end time is required.", ( val, { parent } ) => {
            if ( parent.type !== "scheduled-quiz" ) return true
            if ( !val || val.length === 0 || !val.match( /^(\d\d:\d\d)$/ ) ) return false
            return true
        } ).test( "TEST_FOR_VALID_END_TIME", "For a scheduled quiz a valid end time must be greater than start time.", ( val, { parent } ) => {
            try {
                const start = new Date( `2024-01-01 ${parent.start_time}` )
                const end = new Date( `2024-01-01 ${val}` )
                if ( end <= start ) return false
                return true
            } catch ( err ) {
                return true
            }
        } ),
    } )


    const onImportChange = async ( e ) => {
        if ( e.target.files[0].name.includes( ".txt" ) ) {
            setQuestionsPreview( { status: "loading" } )
            const fileReader = new FileReader()
            fileReader.onload = () => {
                const { questions, errors, preview, errorLines } = validateQuestionsImportFormatForQuestionBank( fileReader.result, "txt", "0", "0" )
                setQuestionsPreview( { status: "loaded", data: preview, file: e.target.files[0], type: "txt", plainData: fileReader.result, errors, questions, errorLines } )
                e.target.value = ""
            }
            fileReader.readAsText( e.target.files[0] )
        } else if ( e.target.files[0].name.includes( ".docx" ) ) {
            try {
                const fileReader = new FileReader()
                fileReader.onload = async () => {
                    setQuestionsPreview( { status: "loading" } )
                    const formData = new FormData()
                    formData.append( 'file', e.target.files[0] )
                    try {
                        const res = await api.convertDocxToHtml( formData )
                        const { questions, errors, preview, errorLines } = validateQuestionsImportFormatForQuestionBank( res.data, "docx", "0", "0" )
                        setQuestionsPreview( { status: "loaded", data: preview, file: e.target.files[0], type: "doc", plainData: convert( fileReader.result ), errors, questions, errorLines } )
                        e.target.value = ""
                    } catch ( err ) {
                        console.log( err )
                        toast( "Something went wrong! try again later" )
                        e.target.value = ""
                    }
                }
                fileReader.readAsText( e.target.files[0] )
            } catch ( err ) {
                console.log( err )
                e.target.value = ""
            }
        } else {
            e.target.value = ""
            toast( "Unsupported file format! Can only import questions from .docx or .txt files." )
        }
    }

    const handleQuestionImport = ( questions ) => {
        const prevQuestions = [...importedQuestions]
        prevQuestions.push( ...questions )
        setImportedQuestions( prevQuestions )
        setQuestionsPreview( false )
    }

    const handleSubjectChange = async ( e, setValue ) => {
        setValue( 'subject', UserStore.getUserSubjectsDetails?.currentSubjects?.find( s => s.subject_id === e.target.value ) )
        setValue( 'questionbank', "-" )
    }


    const handleTimeChange = ( name, val, setValue ) => {
        const time = new Date( val ).toLocaleTimeString( 'en-IN', { hourCycle: "h24", hour: "2-digit", minute: "2-digit", } )
        setValue( name, time )
    }


    const addQuiz = async ( values, { resetForm } ) => {
        try {
            setAdding( true )
            values.questions = importedQuestions
            const { data: { data } } = await api.quiz.createQuickQuiz( values )
            QuizAndQuestionBankStore.fetchClassAssociatedQuizInstances()
            handleClose( true, data.instance || {} )
            resetForm()
        } catch ( err ) {
            toast( err?.response?.data?.message || "Failed to create quiz! please try again." )
        }
        setAdding( false )
    }


    return (
        <Formik onSubmit={addQuiz} initialValues={initialValues} validationSchema={validationSchema}>

            {( { values, setFieldValue, resetForm, errors } ) => {
                const removeImportedQuestion = ( index ) => {
                    const nextQuestions = importedQuestions.filter( ( _, questionIndex ) => questionIndex !== index )
                    setImportedQuestions( nextQuestions )

                    if ( values.max_questions > nextQuestions.length ) {
                        setFieldValue( 'max_questions', nextQuestions.length )
                    }
                }

                return (
                    <Box>
                        <QuestionsPreviewDialog handleImport={handleQuestionImport} isImporting={isImporting} bankId={"0"} topicId={"0"} fromComponent="questionbank" setIsImporting={setIsImporting} getQuestions={() => { }} parent="questionbank" setQuestionsPreview={setQuestionsPreview} questionsPreview={questionsPreview} />
                        <DialogContent sx={{ paddingTop: "0" }}>
                            <Form>
                                <Box borderRadius="5px" >
                                    {classType !== 'hybrid-class' && <Box display="flex" alignItems="center" gap="5px" marginTop="10px">
                                        <Typography sx={{ color: "text.secondary" }} variant="subtitle2">
                                            {`${values.subject?.subject_name} • ${formatDate( values.start_date, { format: "DD-MM-YYYY" } )}`}
                                        </Typography>
                                    </Box>}

                                    <FormControl fullWidth margin="normal">
                                        <Typography gutterBottom variant='subtitle2' color={palette.form.labelColor}>What would you like to name this quiz? *</Typography>
                                        <Field size="small" as={TextField} placeholder="Eg: Data structures quiz" type="text" name="name" />
                                        <Typography fontSize="12px" color='errorMessage.main'><ErrorMessage name='name' /></Typography>
                                    </FormControl>

                                    <Box marginTop="20px">
                                        <Typography display="block" gutterBottom variant='subtitle2' color={palette.form.labelColor}> Select questions for the quiz *</Typography>
                                        {importedQuestions.length > 0 && <Box display="flex" flexDirection="column" borderRadius="5px" maxHeight="250px" overflow="auto" marginBottom="10px" border={border[1]}>
                                            <Typography padding="10px" bgcolor={palette.contentBg} display="block" gutterBottom variant='subtitle2' color='primary' > {importedQuestions.length} Questions imported.</Typography>
                                            <Box overflow="auto" padding="5px" className="slim-custom-scrollbar">
                                                {importedQuestions.map( ( question, index ) => {
                                                    return (
                                                        <Box key={`${question.id || index}-${index}`} display="flex" gap="5px" alignItems="center">
                                                            <IconButton size="small" sx={{ fontSize: "12px" }} onClick={() => removeImportedQuestion( index )} color="error">
                                                                {Icons.default.CloseIcon}
                                                            </IconButton>
                                                            <Box className='quiz-dialog-wysiwyg question-text' variant='body2' fontSize="12px">
                                                                <Latex delimiters={latexDelimiters}>{question.question || ""}</Latex>
                                                            </Box>
                                                        </Box>
                                                    )
                                                } )}
                                            </Box>
                                        </Box>}
                                        <Box position="relative" display="flex" gap="10px" alignItems="center" padding="20px" borderRadius="5px" border={`2px dotted ${palette.borderColor}`} bgcolor={palette.contentBg}>
                                            <Box onChange={onImportChange} component="input" className='custom-file-input' type='file' accept='.txt,.docx' />
                                            {Icons.default.DownloadIcon}
                                            <Box>
                                                <Typography fontSize="14px" >Drag and drop questions file or browse questions file to import questions</Typography>
                                                <Typography fontSize="12px" color='textSecondary' component="em" display="block" >Supported file formats: .txt or .docx</Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                    {importedQuestions.length > 0 && <Box marginTop="20px">
                                        <Typography display="block" gutterBottom variant='subtitle2' color={palette.form.labelColor}> Maximum number of questions to take from the imported questions *</Typography>
                                        <Tooltip placement='top' title="Number of question to include from this topic" >
                                            <TextField sx={{ width: "fit-content" }} type="number" size='small' onChange={( e ) => setFieldValue( 'max_questions', e.target.value )} onWheel={e => e.target.blur()} value={values.max_questions} slotProps={{
                                                input: { sx: { width: "fit-content" }, startAdornment: <Typography fontSize="12px" width="100px" color="GrayText" marginTop="2px" paddingRight="10px" fontWeight="500">Max: {importedQuestions?.length || 0}</Typography>, inputProps: { min: 1, max: importedQuestions?.length } }
                                            }} />
                                        </Tooltip>
                                    </Box>}

                                    {classType !== 'hybrid-class' && <Collapse in={values.type === "scheduled_quiz"}>
                                        <Box>
                                            <Box display="flex" flexWrap="wrap" gap="20px" alignItems="flex-start">
                                                <FormControl margin="normal">
                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                        <Typography variant="subtitle2" gutterBottom color={palette.form.labelColor} >Start time *</Typography>
                                                        <TimePicker
                                                            {...( components?.MuiPickerOutlinedInput?.defaultProps || {} )}
                                                            sx={{ "width": "100%", fontSize: "14px", '& input': { padding: "10px" } }}
                                                            disableToolbar
                                                            defaultValue={dayjs( new Date( `2024-01-01 ${room.sTime}` ) )}
                                                            onChange={val => handleTimeChange( 'start_time', val, setFieldValue )}
                                                        />
                                                    </LocalizationProvider>
                                                </FormControl>
                                                <FormControl margin="normal">
                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                        <Typography variant="subtitle2" gutterBottom color={palette.form.labelColor} >End time *</Typography>
                                                        <TimePicker
                                                            {...( components?.MuiPickerOutlinedInput?.defaultProps || {} )}
                                                            sx={{ "width": "100%", fontSize: "14px", '& input': { padding: "10px" } }}
                                                            defaultValue={dayjs( new Date( `2024-01-01 ${room.eTime}` ) )}
                                                            onChange={val => handleTimeChange( 'end_time', val, setFieldValue )}
                                                        />
                                                    </LocalizationProvider>
                                                </FormControl>
                                            </Box>
                                            <FormControl margin="normal">
                                                <Typography variant="subtitle2" gutterBottom color={palette.form.labelColor} >Quiz duration *</Typography>
                                                <Field
                                                    size="small"
                                                    as={CustomSelectField}
                                                    name="quiz_duration"
                                                    data={QUIZ_DURATION_OPTIONS}
                                                    itemValueKey="value"
                                                    renderOptionText={item => item.label}
                                                    placeholder="Select quiz duration"
                                                />
                                            </FormControl>
                                        </Box>
                                        <Typography color="errorMessage.main" fontSize="12px"> <ErrorMessage name="start_date" /> </Typography>
                                        <Typography color="errorMessage.main" fontSize="12px"> <ErrorMessage name="start_time" /> </Typography>
                                        <Typography color="errorMessage.main" fontSize="12px"> <ErrorMessage name="end_time" /> </Typography>

                                        <Typography fontSize="12px" color="errorMessage.main"> <ErrorMessage name="access_group" /> </Typography>
                                    </Collapse>}
                                    <Box marginTop="40px" display="flex" gap="10px" flexWrap="wrap">
                                        <Button type="submit" disabled={adding} startIcon={adding ? <CircularProgress size={14} /> : Icons.default.AddIcon} sx={{ textTransform: "capitalize" }} variant="contained" disableElevation >{adding ? "Adding new quiz..." : "Add Quiz"}</Button>
                                        <Button onClick={() => handleClose()} sx={{ textTransform: "capitalize" }} variant="contained" disableElevation color="error" >Cancel</Button>
                                    </Box>
                                </Box>
                            </Form>
                        </DialogContent>
                    </Box>
                )
            }}
        </Formik>
    )
} )

export default CreateQuickQuiz